import { BlobItem, BlobServiceClient, ContainerClient } from '@azure/storage-blob';
import { Observable } from 'rxjs';
import { ModelCampaignMetaData } from '../../models/azure-storage/azure-storage-blob-metadata.model';
import { ModelCampaignListDetails } from '../../models/campaign/campaign-list-details.model';
import { ModelCampaign } from '../../models/campaign/campaign.model';
import { ModelRedirectCampaign } from '../../models/campaign/redirect-campaign.model';
import { ModelMenu } from '../../models/menu/menu.model';
import { ModelAzureStorageFileUploadResponse } from '../../models/azure-storage/azure-storage-file-upload-response.model';
import { ModelSubscriptionRecord } from '../../models/subscription/subscription-record.model';

export abstract class AbstractServiceAzureStorage {
  /**
   * Adds a campaign to the master campaign list if it doesn't already exist.
   *
   * @param campaignMetaData - The campaign metadata containing campaign information including campaign ID
   * @returns void - This method does not return a value
   *
   * @remarks
   * - If a campaign with the same ID already exists in the list, the method logs an event and returns early
   * - If the campaign is new, it creates a new campaign list item from the metadata and adds it to the campaigns array
   * - This method is used to ensure that the campaign list is updated with new campaigns without querying the storage again
   * - It is typically called after a new campaign is created or when campaign metadata is updated
   */
  abstract addCampaignToMasterList(campaignMetaData: ModelCampaignMetaData): void;
  abstract appendSubscription(directory: string, record: ModelSubscriptionRecord): Promise<void>;

  /**
   * Creates a subscription file as an append blob in Azure Storage for a specific campaign.
   *
   * @param organizationId - The unique identifier for the organization
   * @param campaignId - The unique identifier for the campaign
   * @param blobMetaData - Metadata object containing campaign information to be attached to the blob
   * @returns A Promise that resolves to true if the operation failed (status > 299 and not 409),
   *          false if the blob was successfully created or already existed
   *
   * @remarks
   * - Sets the blob metadata index to '-2'
   * - Creates the blob with JSON content type
   * - If the blob is newly created, initializes it with an opening bracket '['
   * - Returns true on failure (unexpected status codes) and false on success
   * - Status code 409 (Conflict) is treated as success since it indicates the blob already exists
   */
  abstract createSubscriptionFile(
    organizationId: string,
    campaignId: string,
    blobMetaData: ModelCampaignMetaData
  ): Promise<boolean>;
  abstract filterBlobsByMetadata(
    organizationId: string,
    filters: { startDate?: Date; toDate?: Date; name?: string }
  ): Promise<ModelCampaignListDetails[]>;
  abstract getBlobItemAbsoluteURL(item: BlobItem): string;
  abstract getBlobItemFileNameFromAbsoluteURL(url: string): string;
  abstract initializeBlobStorageCRUDClient(): Observable<BlobServiceClient>;
  abstract listCampaignsForOrganization(organizationId: string): Promise<ModelCampaignListDetails[]>;
  abstract loadCampaignFile(organizationId: string, campaignId: string): Observable<ModelCampaign>;
  abstract loadCampaignSubscriptionFile(organizationId: string, campaignId: string): Observable<ModelSubscriptionRecord[]>;
  abstract loadFile<T>(fileName: string): Observable<T>;
  abstract loadMenuFile(organizationId: string, campaignId: string): Observable<ModelMenu>;
  abstract loadOrganizationSubscriptionFile(organizationId: string): Observable<PushSubscription[]>;
  abstract loadRedirectCampaignFile(organizationId: string, campaignId: string): Observable<ModelRedirectCampaign>;

  /**
   * Uploads multiple files to Azure Blob Storage in parallel for a specific campaign.
   *
   * This method processes an array of files concurrently, uploading each to the specified
   * organization and campaign directory within the configured Azure Storage container. 
   * It handles metadata assignment, content type detection, and provides detailed results 
   * for both successful and failed uploads.
   *
   * @param files - Array of file objects containing filename, File data, and metadata
   * @param files[].filename - The desired filename for the uploaded file
   * @param files[].file - The File object to be uploaded
   * @param files[].metadata - Key-value pairs of metadata to associate with the file
   * @param organizationId - The unique identifier for the organization
   * @param campaignId - The unique identifier for the campaign
   * @param containerName - Optional container name (defaults to base container from config)
   *
   * @returns Promise that resolves to ModelAzureStorageFileUploadResponse containing:
   *   - successful: Array of filenames that were uploaded successfully
   *   - failed: Array of objects with file names and their corresponding error details
   *
   * @throws Will not throw directly, but captures and returns individual file upload errors
   *
   * @example
   * ```typescript
   * const files = [
   *   { filename: 'doc1.pdf', file: pdfFile, metadata: { type: 'document' } },
   *   { filename: 'img1.jpg', file: imageFile, metadata: { category: 'photo' } }
   * ];
   *
   * const result = await service.uploadFilesParallel(files, 'org123', 'campaign456');
   * console.log(`${result.successful.length} files uploaded successfully`);
   * console.log(`${result.failed.length} files failed to upload`);
   * ```
   */
  abstract uploadFilesParallel(
    files: { filename: string; file: File; metadata: Record<string, string> }[],
    organizationId: string,
    campaignId: string,
    containerName?: string
  ): Promise<ModelAzureStorageFileUploadResponse>;
}
